// A stable colour and initials mark per general agency.
//
// Shared so a firm looks the same wherever it appears: ticked in step 4, and
// again as an outgoing email in step 6. A firm recognised by its colour in one
// step and not the other is worse than no colour at all.
//
// Tones are the Directory's tile colours, picked by id so a firm keeps the same
// one between visits without storing anything. Eight of them: with four, any
// RFP with five firms had to repeat one.

const GA_TONES = [
  {
    mark: "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300",
    dot: "bg-blue-500",
    border: "border-blue-400 dark:border-blue-700",
    ring: "ring-blue-500/15",
    soft: "bg-blue-50 dark:bg-blue-950/40",
  },
  {
    mark: "bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300",
    dot: "bg-purple-500",
    border: "border-purple-400 dark:border-purple-700",
    ring: "ring-purple-500/15",
    soft: "bg-purple-50 dark:bg-purple-950/40",
  },
  {
    mark: "bg-cyan-100 dark:bg-cyan-900 text-cyan-700 dark:text-cyan-300",
    dot: "bg-cyan-500",
    border: "border-cyan-400 dark:border-cyan-700",
    ring: "ring-cyan-500/15",
    soft: "bg-cyan-50 dark:bg-cyan-950/40",
  },
  {
    mark: "bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300",
    dot: "bg-orange-500",
    border: "border-orange-400 dark:border-orange-700",
    ring: "ring-orange-500/15",
    soft: "bg-orange-50 dark:bg-orange-950/40",
  },
  {
    mark: "bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300",
    dot: "bg-emerald-500",
    border: "border-emerald-400 dark:border-emerald-700",
    ring: "ring-emerald-500/15",
    soft: "bg-emerald-50 dark:bg-emerald-950/40",
  },
  {
    mark: "bg-rose-100 dark:bg-rose-900 text-rose-700 dark:text-rose-300",
    dot: "bg-rose-500",
    border: "border-rose-400 dark:border-rose-700",
    ring: "ring-rose-500/15",
    soft: "bg-rose-50 dark:bg-rose-950/40",
  },
  {
    mark: "bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300",
    dot: "bg-amber-500",
    border: "border-amber-400 dark:border-amber-700",
    ring: "ring-amber-500/15",
    soft: "bg-amber-50 dark:bg-amber-950/40",
  },
  {
    mark: "bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300",
    dot: "bg-indigo-500",
    border: "border-indigo-400 dark:border-indigo-700",
    ring: "ring-indigo-500/15",
    soft: "bg-indigo-50 dark:bg-indigo-950/40",
  },
  {
    mark: "bg-teal-100 dark:bg-teal-900 text-teal-700 dark:text-teal-300",
    dot: "bg-teal-500",
    border: "border-teal-400 dark:border-teal-700",
    ring: "ring-teal-500/15",
    soft: "bg-teal-50 dark:bg-teal-950/40",
  },
];

export type GaTone = (typeof GA_TONES)[number];

/**
 * Stable per id, but spread first.
 *
 * `id % 4` handed the same tone to every firm four apart, and carrier ids are
 * sequential, so picking Anthem (26), Sharp (30) and MediExcel (34) off one
 * list gave three identical cards.
 *
 * Multiplicative hash: multiply by a large odd constant and read the HIGH bits.
 * The low bits stay linear in the input however large the constant, so a plain
 * modulo of the product keeps the same runs; the high bits do not.
 *
 * 12 bits, not 4: the count is not a power of two, so a 0-15 slice would hand
 * the first seven tones twice the traffic of the last two. 0-4095 over nine
 * leaves the bias too small to see.
 */
export const toneFor = (generalAgencyId: number): GaTone =>
  GA_TONES[((Math.imul(generalAgencyId, 2654435761) >>> 0) >>> 20) % GA_TONES.length];

export const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
